import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useMemo, useState } from 'react';
import { authApi } from '../auth/api';
import '../styles/modules-supervisor-dashboard.css';
import {
  PlatformApiError,
  platformApi,
  type PlatformAccountResult,
  type PlatformAccountRole,
} from './platform-api';

const ROLE_LABELS: Readonly<Record<PlatformAccountRole, string>> = {
  ORGANIZATION_ADMIN: 'Administrateur',
  PROVIDER: 'Prestataire',
  CLIENT: 'Client',
};

const STATUS_LABELS = {
  PENDING_INVITATION: 'En attente',
  ACTIVE: 'Actif',
  SUSPENDED: 'Désactivé',
  ARCHIVED: 'Supprimé',
} as const;

type StatusFilter = 'ALL' | keyof typeof STATUS_LABELS;
type RoleFilter = 'ALL' | PlatformAccountRole;
type InterventionAction = 'suspend' | 'reactivate' | 'archive';

interface PendingIntervention {
  readonly account: PlatformAccountResult;
  readonly action: InterventionAction;
}

export function SupervisorUsers(): React.JSX.Element {
  const [accounts, setAccounts] = useState<readonly PlatformAccountResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<RoleFilter>('ALL');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [organizationId, setOrganizationId] = useState('ALL');
  const [pendingIntervention, setPendingIntervention] = useState<PendingIntervention | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [demoSavingUserId, setDemoSavingUserId] = useState<string | null>(null);
  const [checkingIntervention, setCheckingIntervention] = useState(false);
  const [interventionError, setInterventionError] = useState<string | null>(null);

  async function loadAccounts(cancelled?: () => boolean): Promise<void> {
    setError(null);
    try {
      const result = await platformApi.listAccounts();
      if (!cancelled?.()) setAccounts(result);
    } catch {
      if (!cancelled?.()) setError('La liste des comptes ne peut pas être chargée.');
    } finally {
      if (!cancelled?.()) setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadAccounts(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, []);

  const organizations = useMemo(() => {
    const byId = new Map<string, string>();
    accounts.forEach((account) => {
      if (account.organization !== null) {
        byId.set(account.organization.id, account.organization.displayName);
      }
    });
    return [...byId.entries()].sort((left, right) =>
      left[1].localeCompare(right[1], 'fr', { sensitivity: 'base' }),
    );
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('fr-FR');
    return accounts.filter((account) => {
      if (role !== 'ALL' && account.role !== role) return false;
      if (status !== 'ALL' && account.accountStatus !== status) return false;
      if (organizationId !== 'ALL' && account.organization?.id !== organizationId) return false;
      if (normalizedQuery.length === 0) return true;

      return [
        account.givenName,
        account.familyName,
        account.email,
        account.organization?.displayName ?? 'Sans rattachement',
        account.role === null ? 'Sans rôle' : ROLE_LABELS[account.role],
      ]
        .join(' ')
        .toLocaleLowerCase('fr-FR')
        .includes(normalizedQuery);
    });
  }, [accounts, organizationId, query, role, status]);

  async function requestIntervention(
    account: PlatformAccountResult,
    action: InterventionAction,
  ): Promise<void> {
    if (
      (account.membershipId === null && action !== 'archive') ||
      checkingIntervention ||
      submitting
    ) {
      return;
    }

    setNotice(null);
    setError(null);
    setInterventionError(null);
    setReason('');
    setCheckingIntervention(true);

    try {
      await platformApi.checkAccountInterventionReadiness();
      setPendingIntervention({ account, action });
    } catch (requestError) {
      setError(interventionErrorMessage(requestError));
    } finally {
      setCheckingIntervention(false);
    }
  }

  async function saveFinanceDemo(account: PlatformAccountResult, enabled: boolean): Promise<void> {
    if (
      demoSavingUserId !== null ||
      account.role !== 'ORGANIZATION_ADMIN' ||
      account.accountStatus !== 'ACTIVE' ||
      account.membershipStatus !== 'ACTIVE'
    )
      return;
    setDemoSavingUserId(account.userId);
    setNotice(null);
    setError(null);
    try {
      const { csrfToken } = await authApi.csrfToken();
      const result = await platformApi.setFinanceDemo(account.userId, enabled, csrfToken);
      setAccounts((current) =>
        current.map((item) =>
          item.userId === account.userId ? { ...item, financeDemoEnabled: result.enabled } : item,
        ),
      );
      setNotice(
        'Mode démo financier ' +
          (result.enabled ? 'autorisé' : 'désactivé') +
          ' pour ' +
          account.givenName +
          ' ' +
          account.familyName +
          '.',
      );
    } catch {
      setError('Le réglage du mode démo n’a pas pu être enregistré.');
    } finally {
      setDemoSavingUserId(null);
    }
  }

  function closeIntervention(): void {
    if (submitting) return;
    setPendingIntervention(null);
    setReason('');
    setInterventionError(null);
  }

  async function confirmIntervention(): Promise<void> {
    if (pendingIntervention === null || submitting) return;
    const { account, action } = pendingIntervention;
    const normalizedReason = reason.trim();
    if (action !== 'reactivate' && normalizedReason.length === 0) return;

    setSubmitting(true);
    setInterventionError(null);
    try {
      const { csrfToken } = await authApi.csrfToken();
      if (action === 'suspend') {
        if (account.membershipId === null) return;
        await platformApi.suspendAccount(account.membershipId, normalizedReason, csrfToken);
      } else if (action === 'reactivate') {
        if (account.membershipId === null) return;
        await platformApi.reactivateAccount(account.membershipId, csrfToken);
      } else if (account.membershipId === null) {
        await platformApi.archiveUnattachedAccount(account.userId, normalizedReason, csrfToken);
      } else {
        await platformApi.archiveAccount(account.membershipId, normalizedReason, csrfToken);
      }

      await loadAccounts();
      setNotice(successMessage(account, action));
      setPendingIntervention(null);
      setReason('');
      setInterventionError(null);
    } catch (requestError) {
      // Le pré-contrôle empêche normalement l'ouverture d'une pop-in avec une session déjà trop
      // ancienne. Cette restitution locale reste nécessaire si la session expire entre l'ouverture
      // de la confirmation et le clic final, ou pour toute autre erreur de mutation.
      setInterventionError(interventionErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="app-content supervisor-workspace supervisor-users">
      <header className="dashboard-heading supervisor-users__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Support administratif</p>
          <h1>Utilisateurs</h1>
          <p className="dashboard-heading__intro">
            Retrouvez les Administrateurs, Prestataires et Clients avec leur organisation et leur
            état d’accès. Les interventions sensibles restent confirmées, tracées et contrôlées côté
            serveur.
          </p>
        </div>
      </header>

      <section className="supervisor-users__filters" aria-label="Filtres des comptes">
        <label>
          <span>Recherche</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nom, e-mail ou organisation…"
          />
        </label>
        <label>
          <span>Type de compte</span>
          <select value={role} onChange={(event) => setRole(event.target.value as RoleFilter)}>
            <option value="ALL">Tous</option>
            <option value="ORGANIZATION_ADMIN">Administrateurs</option>
            <option value="PROVIDER">Prestataires</option>
            <option value="CLIENT">Clients</option>
          </select>
        </label>
        <label>
          <span>État</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as StatusFilter)}
          >
            <option value="ALL">Tous</option>
            <option value="ACTIVE">Actifs</option>
            <option value="SUSPENDED">Désactivés</option>
            <option value="PENDING_INVITATION">En attente</option>
            <option value="ARCHIVED">Supprimés</option>
          </select>
        </label>
        <label>
          <span>Organisation</span>
          <select
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
          >
            <option value="ALL">Toutes</option>
            {organizations.map(([id, displayName]) => (
              <option key={id} value={id}>
                {displayName}
              </option>
            ))}
          </select>
        </label>
      </section>

      {notice ? (
        <p
          className="supervisor-notice supervisor-users__notice tosho-feedback is-success"
          role="status"
        >
          {notice}
        </p>
      ) : null}
      {loading ? <p className="supervisor-dashboard__status">Chargement des comptes…</p> : null}
      {error ? (
        <div
          className="supervisor-danger-error supervisor-users__error tosho-feedback is-error"
          role="alert"
        >
          {error}
        </div>
      ) : null}

      {!loading ? (
        <section className="supervisor-panel supervisor-users__panel">
          <header className="supervisor-panel__header">
            <div>
              <p>Annuaire plateforme</p>
              <h2>Comptes et rattachements</h2>
            </div>
            <span className="supervisor-users__count">
              {filteredAccounts.length.toLocaleString('fr-FR')} /{' '}
              {accounts.length.toLocaleString('fr-FR')}
            </span>
          </header>

          {filteredAccounts.length === 0 ? (
            <div className="supervisor-state">
              <p>Aucun compte ne correspond aux filtres sélectionnés.</p>
            </div>
          ) : (
            <div className="supervisor-table-scroll">
              <table className="supervisor-table supervisor-users__table">
                <thead>
                  <tr>
                    <th>Compte</th>
                    <th>Type</th>
                    <th>Rattachement</th>
                    <th>État</th>
                    <th>Dernière activité</th>
                    <th>Démo finances</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account) => (
                    <AccountRow
                      account={account}
                      key={account.userId}
                      disabled={submitting || checkingIntervention}
                      onAction={(action) => void requestIntervention(account, action)}
                      onFinanceDemo={(enabled) => void saveFinanceDemo(account, enabled)}
                      demoDisabled={demoSavingUserId !== null}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {pendingIntervention ? (
        <InterventionDialog
          intervention={pendingIntervention}
          reason={reason}
          error={interventionError}
          submitting={submitting}
          onReasonChange={(value) => {
            setReason(value);
            setInterventionError(null);
          }}
          onCancel={closeIntervention}
          onConfirm={() => void confirmIntervention()}
        />
      ) : null}
    </main>
  );
}

function AccountRow({
  account,
  disabled,
  onAction,
  onFinanceDemo,
  demoDisabled,
}: {
  readonly account: PlatformAccountResult;
  readonly disabled: boolean;
  readonly onAction: (action: InterventionAction) => void;
  readonly onFinanceDemo: (enabled: boolean) => void;
  readonly demoDisabled: boolean;
}): React.JSX.Element {
  return (
    <tr>
      <td>
        <strong>
          {account.givenName} {account.familyName}
        </strong>
        <small>{account.email}</small>
      </td>
      <td>
        {account.role === null ? (
          <span className="supervisor-muted">Sans rôle</span>
        ) : (
          ROLE_LABELS[account.role]
        )}
      </td>
      <td>
        {account.organization === null ? (
          <>
            <strong>Sans rattachement</strong>
            <small>Aucun accès d’organisation</small>
          </>
        ) : (
          <>
            <strong>{account.organization.displayName}</strong>
            <small>{membershipLabel(account)}</small>
          </>
        )}
      </td>
      <td>
        <span
          className={`supervisor-status supervisor-status--${statusClass(account.accountStatus)}`}
        >
          {STATUS_LABELS[account.accountStatus]}
        </span>
      </td>
      <td>
        {account.lastActivityAt === null
          ? 'Jamais utilisé'
          : formatDateTime(account.lastActivityAt)}
      </td>
      <td>
        {account.role === 'ORGANIZATION_ADMIN' &&
        account.accountStatus === 'ACTIVE' &&
        account.membershipStatus === 'ACTIVE' ? (
          <label className="supervisor-users__finance-demo">
            <input
              type="checkbox"
              role="switch"
              aria-label={'Mode démo finances pour ' + account.givenName + ' ' + account.familyName}
              checked={account.financeDemoEnabled === true}
              disabled={demoDisabled}
              onChange={(event) => onFinanceDemo(event.currentTarget.checked)}
            />
            <span>{account.financeDemoEnabled ? 'Activé' : 'Désactivé'}</span>
          </label>
        ) : (
          <span className="supervisor-muted">Non disponible</span>
        )}
      </td>
      <td className="supervisor-table__actions supervisor-users__actions">
        <AccountActions account={account} disabled={disabled} onAction={onAction} />
      </td>
    </tr>
  );
}

function AccountActions({
  account,
  disabled,
  onAction,
}: {
  readonly account: PlatformAccountResult;
  readonly disabled: boolean;
  readonly onAction: (action: InterventionAction) => void;
}): React.JSX.Element {
  if (account.accountStatus === 'ARCHIVED') {
    return <span className="supervisor-muted">Aucune action</span>;
  }

  if (account.membershipId === null) {
    return (
      <button
        className="supervisor-button supervisor-users__archive-button"
        type="button"
        disabled={disabled}
        onClick={() => onAction('archive')}
      >
        Supprimer
      </button>
    );
  }

  return (
    <span className="supervisor-users__action-group">
      {account.accountStatus === 'ACTIVE' ? (
        <button
          className="supervisor-button supervisor-button--secondary"
          type="button"
          disabled={disabled}
          onClick={() => onAction('suspend')}
        >
          Désactiver
        </button>
      ) : null}
      {account.accountStatus === 'SUSPENDED' ? (
        <button
          className="supervisor-button supervisor-button--secondary"
          type="button"
          disabled={disabled}
          onClick={() => onAction('reactivate')}
        >
          Réactiver
        </button>
      ) : null}
      <button
        className="supervisor-button supervisor-users__archive-button"
        type="button"
        disabled={disabled}
        onClick={() => onAction('archive')}
      >
        Supprimer
      </button>
    </span>
  );
}

function InterventionDialog({
  intervention,
  reason,
  error,
  submitting,
  onReasonChange,
  onCancel,
  onConfirm,
}: {
  readonly intervention: PendingIntervention;
  readonly reason: string;
  readonly error: string | null;
  readonly submitting: boolean;
  readonly onReasonChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  const { account, action } = intervention;
  const needsReason = action !== 'reactivate';
  const definition = interventionDefinition(action);
  const canConfirm = !submitting && (!needsReason || reason.trim().length > 0);

  return (
    <ModalBackdrop
      className="supervisor-confirmation-backdrop"
      aria-labelledby="supervisor-account-intervention-title"
      close={<ModalCloseButton disabled={submitting} onClick={onCancel} />}
    >
      <section className="tosho-modal-panel supervisor-confirmation-dialog supervisor-users__confirmation">
        <header className="tosho-modal-header">
          <p>{definition.eyebrow}</p>
          <h2 id="supervisor-account-intervention-title">{definition.title}</h2>
        </header>
        <div className="supervisor-users__confirmation-body">
          <p>
            <strong>
              {account.givenName} {account.familyName}
            </strong>{' '}
            — {account.role === null ? 'Compte' : ROLE_LABELS[account.role]} de{' '}
            {account.organization?.displayName ?? 'aucune organisation'}.
          </p>
          <p>{definition.explanation}</p>
          {account.role === 'ORGANIZATION_ADMIN' && action !== 'reactivate' ? (
            <p className="supervisor-users__admin-warning">
              Cette intervention reste autorisée même si l’organisation se retrouve temporairement
              sans Administrateur actif.
            </p>
          ) : null}
          {error ? (
            <div
              className="supervisor-danger-error supervisor-users__confirmation-error tosho-feedback is-error"
              role="alert"
            >
              {error}
            </div>
          ) : null}
          {needsReason ? (
            <label className="supervisor-users__reason-field">
              <span>Motif de l’intervention</span>
              <textarea
                value={reason}
                maxLength={500}
                rows={4}
                autoFocus
                placeholder="Indiquez pourquoi cette intervention est nécessaire…"
                onChange={(event) => onReasonChange(event.target.value)}
              />
              <small>{reason.length} / 500 caractères</small>
            </label>
          ) : null}
        </div>
        <footer className="supervisor-users__confirmation-actions">
          <button
            className="supervisor-button supervisor-button--secondary"
            type="button"
            disabled={submitting}
            onClick={onCancel}
          >
            Annuler
          </button>
          <button
            className={`supervisor-button${definition.dangerous ? ' supervisor-button--danger' : ' supervisor-users__confirm-button'}`}
            type="button"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {submitting ? 'Traitement…' : definition.confirmLabel}
          </button>
        </footer>
      </section>
    </ModalBackdrop>
  );
}

function interventionDefinition(action: InterventionAction): {
  readonly eyebrow: string;
  readonly title: string;
  readonly explanation: string;
  readonly confirmLabel: string;
  readonly dangerous: boolean;
} {
  if (action === 'suspend') {
    return {
      eyebrow: 'Désactivation du compte',
      title: 'Désactiver cet accès ?',
      explanation:
        'Le compte ne pourra plus se connecter et ses sessions actives seront immédiatement révoquées. Il pourra être réactivé ultérieurement.',
      confirmLabel: 'Désactiver le compte',
      dangerous: true,
    };
  }
  if (action === 'reactivate') {
    return {
      eyebrow: 'Réactivation du compte',
      title: 'Réactiver cet accès ?',
      explanation:
        'Le compte retrouvera son accès avec ses moyens d’identification existants. Cette action ne permet jamais d’activer un compte qui n’a pas terminé sa première activation.',
      confirmLabel: 'Réactiver le compte',
      dangerous: false,
    };
  }
  return {
    eyebrow: 'Suppression logique',
    title: 'Supprimer ce compte ?',
    explanation:
      'L’accès sera définitivement archivé et ses sessions seront révoquées. L’historique et les preuves nécessaires restent conservés ; aucune donnée n’est supprimée physiquement.',
    confirmLabel: 'Supprimer le compte',
    dangerous: true,
  };
}

function successMessage(account: PlatformAccountResult, action: InterventionAction): string {
  const name = `${account.givenName} ${account.familyName}`;
  if (action === 'suspend') return `Le compte de ${name} a été désactivé.`;
  if (action === 'reactivate') return `Le compte de ${name} a été réactivé.`;
  return `Le compte de ${name} a été supprimé logiquement.`;
}

function interventionErrorMessage(error: unknown): string {
  if (error instanceof PlatformApiError) {
    if (error.status === 409) {
      return 'L’opération est impossible dans l’état actuel du compte. Actualisez la liste puis réessayez.';
    }
    if (error.status === 403) {
      return 'Votre session ne permet plus cette intervention sensible. Reconnectez-vous puis réessayez.';
    }
    if (error.status === 400) return 'Le motif ou la demande n’est pas valide.';
  }
  return 'L’intervention n’a pas pu être appliquée. Vérifiez l’état du compte puis réessayez.';
}

function membershipLabel(account: PlatformAccountResult): string {
  if (account.role === null || account.membershipStatus === null) return 'Sans rattachement';
  const status = STATUS_LABELS[account.membershipStatus];
  return `${ROLE_LABELS[account.role]} · ${status}`;
}

function statusClass(status: PlatformAccountResult['accountStatus']): string {
  return status.toLocaleLowerCase('en-US').replace('_', '-');
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Indisponible';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
